import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { LOGO_URL, MASCOT_URL } from "@/lib/translations";
import { ShieldCheck, ArrowRight } from "lucide-react";

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.28-1.93-6.14-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.86 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.68-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.68 2.84C6.72 7.3 9.14 5.38 12 5.38Z" />
  </svg>
);

export default function Login() {
  const { loginWithGoogle, user, ready } = useAuth();
  const { t, lang, changeLang } = useI18n();
  const location = useLocation();
  const authError = location.state?.authError;

  useEffect(() => {
    if (ready && user) window.location.replace("/");
  }, [ready, user]);

  return (
    <div className="flex min-h-screen">
      {/* Brand / access panel */}
      <div className="relative hidden w-1/2 flex-col overflow-hidden bg-secondary lg:flex">
        <div className="pointer-events-none absolute -left-16 -top-20 h-80 w-80 rounded-full bg-horizonte-orange/15 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute right-0 top-1/3 h-72 w-72 rounded-full bg-horizonte-orange/10 blur-3xl" aria-hidden />
        <div className="relative z-10 flex flex-1 flex-col px-12 pt-12">
          <img src={LOGO_URL} alt="Horizonte Documenta" className="h-16 w-auto object-contain" data-testid="access-logo" />
          <div className="mt-3">
            <img src="/renco-logo.png" alt="Renco" className="h-8 w-auto object-contain" data-testid="access-renco-logo" />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-display text-2xl font-bold text-horizonte-orange" data-testid="access-words">
            {[t("wDocuments"), t("wContracts"), t("wCompliance")].map((w, i) => (
              <React.Fragment key={w}>
                {i > 0 && <span className="text-horizonte-orange/40">–</span>}
                <span>{w}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-horizonte-orange" /> {t("googleNote")}
          </div>
          <div className="relative mt-auto flex flex-1 items-end justify-center pt-6">
            <img src={MASCOT_URL} alt="" className="max-h-[60vh] w-auto object-contain" data-testid="access-mascot" />
          </div>
        </div>
      </div>

      {/* Sign-in panel */}
      <div className="flex w-full flex-col justify-center bg-background px-6 py-12 lg:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex justify-end gap-1">
            {["en", "pt"].map((l) => (
              <button
                key={l}
                data-testid={`login-lang-${l}`}
                onClick={() => changeLang(l)}
                className={`rounded-md px-3 py-1 text-xs font-semibold uppercase transition-colors ${lang === l ? "bg-horizonte-orange text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="mb-8 flex justify-center rounded-2xl border bg-white p-6">
            <img src={LOGO_URL} alt="Horizonte Documenta" className="h-16 w-auto object-contain" data-testid="login-logo" />
          </div>

          <h2 className="font-display text-3xl font-bold tracking-tight">{t("signIn")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("signInSubtitle")}</p>

          {authError && (
            <div data-testid="login-error" className="mt-5 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {authError}
            </div>
          )}

          <Button
            onClick={loginWithGoogle}
            data-testid="google-login-button"
            className="mt-8 h-12 w-full gap-3 bg-white text-foreground shadow-sm ring-1 ring-border hover:bg-secondary"
          >
            <GoogleIcon />
            {t("signInGoogle")}
            <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground" />
          </Button>

          <p className="mt-5 text-center text-xs text-muted-foreground">{t("googleNote")}</p>
        </div>
      </div>
    </div>
  );
}
